import { useMemo } from "react";
import { TimezoneOption } from "@platform/features/timezones/timezones.types";
import { TimeslotId } from "@platform/features/timeslots/timeslots.types";
import { makeTimeslotsForRange } from "@platform/features/timeslots/utils/makeTimeslotsForRange";
import { useContiguousTimeslots } from "@platform/features/timeslots/hooks/useContiguousTimeslots";
import {
  END_HOUR,
  MIN_CONTIGUOUS_TIMESLOT_DURATION,
  MIN_REQUIRED_HOURS,
  START_HOUR,
} from "../scheduling.constants";
import { useTimeSlotSelection } from "./useTimeslotSelection";
import useProgram from "@platform/features/program/hooks/useProgram";

export type Args = {
  savedTimeSlots: TimeslotId[];
  selectedTimezone: TimezoneOption;
};

export function useScheduling({ savedTimeSlots, selectedTimezone }: Args) {
  const { program } = useProgram();

  // Generate time slots for the configured start and end hours
  const timeSlots = useMemo(() => {
    if (!selectedTimezone?.value) return [];

    return makeTimeslotsForRange({
      startHour: START_HOUR,
      endHour: END_HOUR,
      timezone: selectedTimezone,
      startDate: program?.scheduled_engagement_start_date || "",
    });
  }, [selectedTimezone]);

  // Hook to manage time slot selection state and callbacks
  const {
    selectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = useTimeSlotSelection({
    allTimeSlots: timeSlots,
    savedTimeSlots,
  });

  // Manage contiguous slots and two-hour slot count
  const { contiguousTimeslots, validHoursCount } = useContiguousTimeslots(
    selectedTimeSlots,
    MIN_CONTIGUOUS_TIMESLOT_DURATION
  );

  // Check if requirements are met
  const requirementsMet = validHoursCount >= MIN_REQUIRED_HOURS;

  return {
    timeSlots,
    selectedTimeSlots,
    handleMouseDown,
    handleMouseOver,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    contiguousTimeslots,
    validHoursCount,
    requirementsMet,
  };
}

export default useScheduling;
